import {AvatarSdkAccessToken, fetchAccessToken} from "./avatarSdkAccessTokenApi";
import {tracedFetch} from "./tracing";

export interface PlayerResponse {
    url: string;
    code: string;
    createdOn: string;
    comment: string;
}

export interface AvatarResponse {
    url: string;
    code: string;
    status: string;
    progress: number;
    name: string;
    pipeline: string;
    description: string;
    createdOn: string;
    ctime?: string;
    mesh: string;
    texture: string;
    thumbnail: string;
    haircuts: string;
    blendshapes: string;
    modelInfo: string;
    exports: string;
}

export interface AvatarExportFileResponse {
    file: string;
    format: string;
    type: string;
}

export interface AvatarExportResponse {
    url: string;
    code: string;
    status: string;
    createdOn: string;
    files: AvatarExportFileResponse[];
}

export type Result<T> = {
    success: true;
    data: T;
    tokenData: AvatarSdkAccessToken;
} | {
    success: false;
    error: string;
}

/**
 * Creates a new player with the given comment
 * @param comment Comment to associate with the player
 * @returns Promise resolving to the player response
 */
export async function createPlayer(comment?: string): Promise<Result<PlayerResponse>> {
    let result = await makeAuthenticatedRequest<PlayerResponse>('/players/', {method: 'post', body: {comment}});

    if (!result.success) return result;

    let {tokenData, data: player} = result;

    saveToken(player.code, tokenData);

    return result;
}

export async function pollAvatarStatus(playerUid: string, avatarUid: string): Promise<Result<AvatarResponse>> {
    return await makeAuthenticatedRequest<AvatarResponse>(`/avatars/${avatarUid}/`, {playerUid});
}

/**
 * Gets all exports for a specific avatar
 * @param playerUid Player UID associated with the avatar
 * @param avatarUid Avatar UID to get exports for
 * @returns Promise resolving to a list of avatar exports
 */
export async function getAvatarExports(playerUid: string, avatarUid: string): Promise<Result<AvatarExportResponse[]>> {
    return await makeAuthenticatedRequest<AvatarExportResponse[]>(`/avatars/${avatarUid}/exports/`, {playerUid});
}

/**
 * Gets the download URL for an avatar file
 * @param playerUid Player UID associated with the avatar
 * @param avatarUid Avatar UID to get the download URL for
 * @returns Promise resolving to the download URL for the avatar file
 */
export async function getAvatarSdkFileDownloadUrl(playerUid: string, avatarUid: string): Promise<Result<string>> {
    const exportsResult = await getAvatarExports(playerUid, avatarUid);

    if (!exportsResult.success) {
        return {
            success: false,
            error: exportsResult.error
        };
    }

    const exports = exportsResult.data;

    if (exports.length === 0) {
        return {
            success: false,
            error: "Avatar export list is empty"
        };
    }

    if (exports[0].files.length === 0) {
        return {
            success: false,
            error: "Avatar export file list is empty"
        };
    }

    return {
        success: true,
        data: exports[0].files[0].file,
        tokenData: exportsResult.tokenData
    };
}

/**
 * Downloads a file from a URL using the authenticated client
 * @param url URL to download the file from
 * @param playerUid Optional player UID for authentication
 * @returns Promise resolving to the file as a Blob
 */
export async function download(url: string, playerUid?: string): Promise<Result<Blob>> {
    const headers: Record<string, string> = {};
    const authResult = await setAuthorization(playerUid, headers);

    if (!authResult.success || !authResult.tokenData) {
        return {
            success: false,
            error: 'Unable to set authorization'
        };
    }

    try {
        const response = await tracedFetch('avatarSdk.download', url, {
            method: 'GET',
            headers
        });

        if (!response.ok) {
            return {
                success: false,
                error: `Failed to download file: ${response.status} ${response.statusText}`
            };
        }

        const blob = await response.blob();
        return {
            success: true,
            data: blob,
            tokenData: authResult.tokenData
        };
    } catch (error) {
        return {
            success: false,
            error: `Error downloading file: ${error instanceof Error ? error.message : String(error)}`
        };
    }
}

/**
 * Ingests an avatar by downloading it from AvatarSDK
 * @param playerUid Player UID associated with the avatar
 * @param avatarUid Avatar UID to ingest
 * @returns Promise resolving to the avatar file as a Blob
 */
export async function ingestAvatar(
    playerUid: string,
    avatarUid: string
): Promise<Result<Blob>> {
    // Get the download URL for the avatar file
    const fileUrlResult = await getAvatarSdkFileDownloadUrl(playerUid, avatarUid);

    if (!fileUrlResult.success) {
        return {
            success: false,
            error: fileUrlResult.error
        };
    }

    const fileUrl = fileUrlResult.data;

    // Download the avatar file
    const fileResult = await download(fileUrl, playerUid);

    if (!fileResult.success) {
        return {
            success: false,
            error: fileResult.error
        };
    }

    return fileResult;
}

/**
 * Creates a new avatar with the given parameters and photo
 * @param playerUid Player UID to associate with the avatar
 * @param name Name of the avatar
 * @param photo Photo file as a Blob or File object
 * @returns Promise resolving to the avatar response
 */
export async function createAvatar(playerUid: string | undefined, photo: Blob, name: string = 'Player'): Promise<Result<AvatarResponse>> {
    // Create form data
    const formData = new FormData();
    formData.append('name', name);
    formData.append('pipeline', 'head_2.0');
    formData.append('pipeline_subtype', 'head/mobile');

    addParameters(formData);
    addExportParameters(formData);

    formData.append('photo', photo, 'photo.jpg');

    return await makeAuthenticatedRequest<AvatarResponse>('/avatars/', {method: 'post', playerUid, body: formData});
}

const avatarSDKBaseAddress = 'https://api.avatarsdk.com'

async function makeAuthenticatedRequest<TResponse>(
    path: string,
    options?: {
        method?: string;
        playerUid?: string;
        body?: FormData | object | string;
        headers?: Record<string, string>;
    }
): Promise<Result<TResponse>> {
    options = options || {};
    let method = options.method || 'get';
    let playerUid = options.playerUid;
    let body = options.body;

    let headers = {
        ...options.headers
    }

    let {tokenData} = await setAuthorization(playerUid, headers);

    if (!tokenData) return {
        success: false,
        error: 'Unable to retrieve token'
    };

    if (typeof body === 'object' && !(body instanceof FormData)) {
        body = JSON.stringify(body);
        headers['Content-Type'] = 'application/json';
    }

    let response = await tracedFetch(`avatarSdk${path}`, `${avatarSDKBaseAddress}${path}`, {
        method,
        headers,
        body: body as BodyInit
    });

    let data = await response.json();

    if (!response.ok) {
        return {
            success: false,
            error: data.message || 'Request failed'
        };
    }

    return {
        success: true,
        data: data as TResponse,
        tokenData
    };
}

async function setAuthorization(playerUid: string | undefined, headers: Record<string, string>) {
    let tokenData = null;
    if (playerUid) {
        tokenData = loadToken(playerUid);
    }
    if (!tokenData) {
        const authResult = await fetchAccessToken();
        if (!authResult.success) {
            return {
                success: false,
                error: authResult.error
            };
        }

        tokenData = authResult.data;
    }

    headers['Authorization'] = `${tokenData.tokenType} ${tokenData.accessToken}`;

    return {success: true, tokenData};
}

const storageKey = (playerUid: string) => `avatar_sdk_token_${playerUid}`;

function loadToken(playerUid: string): AvatarSdkAccessToken | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const key = storageKey(playerUid);
    const storedTokenData = sessionStorage.getItem(key);

    if (!storedTokenData) return null;

    return JSON.parse(storedTokenData);
}

function saveToken(playerUid: string, tokenData: AvatarSdkAccessToken) {
    if (typeof window === 'undefined') {
        return;
    }

    const key = storageKey(playerUid);
    sessionStorage.setItem(key, JSON.stringify(tokenData));
}

function addParameters(formData: FormData) {
    const parameters = {
        model_info: {
            plus: [
                "gender",
                "age",
                "race",
                "hair_color",
                "eye_iris_color"
            ]
        },
        avatar_modifications: {
            plus: {
                remove_smile: true,
                remove_glasses: true,
                enhance_lighting: true
            }
        }
    };
    formData.append('parameters', JSON.stringify(parameters));
}

function addExportParameters(formData: FormData) {
    const exportParameters = {
        format: "glb",
        embed: true,
        embed_textures: true,
        texture_size: {
            width: 2048,
            height: 2048
        },
        lod: 5,
        blendshapes: {
            list: [
                "mobile_51",
                "visemes_15"
            ],
            embed: true
        },
        haircuts: {
            list: [
                'generated'
            ],
            embed: true
        }
    };
    formData.append('export_parameters', JSON.stringify(exportParameters));
}
